/* eslint-disable @ngrx/prefer-effect-callback-in-block-statement */
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';

import { RoleFacade } from '@cdba/core/auth/role-facade/role.facade';
import { ProductDetailService } from '@cdba/detail/service/detail.service';
import { BillOfMaterialsType } from '@cdba/shared/constants';
import { CalculationsResponse } from '@cdba/shared/models/responses';

import {
  loadCalculations,
  loadCalculationsFailure,
  loadCalculationsSuccess,
  loadProductDetailsSuccess,
} from '../../../actions';
import { selectCalculationsContext } from '../../../selectors';

@Injectable()
export class CalculationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly productDetailService = inject(ProductDetailService);
  private readonly store = inject(Store);
  private readonly roleFacade = inject(RoleFacade);

  loadCalculations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCalculations),
      concatLatestFrom(() => [
        this.store.select(selectCalculationsContext),
        this.roleFacade.hasAnyPricingRole$,
      ]),
      filter(([_action, context]) => context.type !== undefined),
      exhaustMap(([_action, context, hasPricingRole]) => {
        const type = context.type;
        switch (type) {
          case BillOfMaterialsType.SCHAEFFLER: {
            return context.referenceTypeIdentifier && hasPricingRole
              ? this.productDetailService
                  .getCalculations(
                    context.referenceTypeIdentifier.materialNumber,
                    context.referenceTypeIdentifier.plant
                  )
                  .pipe(
                    map((response: CalculationsResponse) =>
                      loadCalculationsSuccess({
                        calculations: response.items,
                        numberOfUnauthorizedCalculations:
                          response.numberOfUnauthorizedCalculations,
                        missingCostRoles: response.missingCostRoles,
                        unauthorizedPlants: response.unauthorizedPlants,
                        referenceTypeIdentifier:
                          context.referenceTypeIdentifier,
                      })
                    ),
                    catchError((error: HttpErrorResponse) =>
                      of(
                        loadCalculationsFailure({
                          errorMessage: error.error.detail || error.message,
                          statusCode: error.status,
                        })
                      )
                    )
                  )
              : of(
                  loadCalculationsFailure({
                    errorMessage: 'User has no valid cost roles.',
                    statusCode: undefined,
                  })
                );
          }
          case BillOfMaterialsType.VITESCO: {
            // There currently no Calculations for Vitesco products
            return of(
              loadCalculationsSuccess({
                calculations: [],
                numberOfUnauthorizedCalculations: 0,
                missingCostRoles: [],
                unauthorizedPlants: [],
                referenceTypeIdentifier: context.referenceTypeIdentifier,
              })
            );
          }
          default: {
            return EMPTY;
          }
        }
      })
    )
  );

  triggerDataLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadProductDetailsSuccess),
      map(() => loadCalculations())
    )
  );
}
